import React, { useEffect, useState } from "react";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { getRecoil } from "recoil-nexus";
import styled from "styled-components";
import ApiController from "../../controller/ApiController";
import LogController from "../../controller/LogController";
import { masterStoreState } from "../../recoil/auth";
import { langState, langText } from "../../recoil/lang";
import {
  checkoutDetailType,
  checkoutState,
  checkoutType,
} from "../../recoil/socket";
import "../../styles/CustomModal.css";
import { getCheckOutContent } from "../../utils/util";

const CheckoutModal = (oProps: {
  isVisible: boolean;
  setVisible: any;
  checkoutNoticeData: checkoutDetailType;
}) => {
  const getCheckoutAudioSource = () => {
    if (checkoutNoticeData?.sound_type === 0) {
      return `${process.env.PUBLIC_URL}/knockknock/audio/checkout01.webm`;
    } else if (checkoutNoticeData?.sound_type === 1) {
      return `${process.env.PUBLIC_URL}/knockknock/audio/checkout02.webm`;
    } else if (checkoutNoticeData?.sound_type === 2) {
      return `${process.env.PUBLIC_URL}/knockknock/audio/checkout03.webm`;
    } else if (checkoutNoticeData?.sound_type === 3) {
      return `${process.env.PUBLIC_URL}/knockknock/audio/checkout04.webm`;
    } else if (checkoutNoticeData?.sound_type === 4) {
      return `${process.env.PUBLIC_URL}/knockknock/audio/checkout05.mp3`;
    }
    return `${process.env.PUBLIC_URL}/knockknock/audio/checkout01.webm`;
  };

  const navigate = useNavigate();
  const checkoutNoticeData = oProps.checkoutNoticeData;
  const checkoutFlag = useRecoilValue<checkoutType>(checkoutState);

  const lang = useRecoilValue<number>(langState);

  const [checkoutAudio, setCheckoutAudio] = useState(
    new Audio(getCheckoutAudioSource())
  );

  const onCloseModal = () => {
    oProps.setVisible(false);
    LogController.click({
      path: "CheckoutModal",
      params: "checkout",
      params2: "사용자가 체크아웃 모달 닫음",
    });
  };

  useEffect(() => {
    if (oProps.isVisible) {
      LogController.event({
        path: "ArrivalModal",
        params: "checkout",
        params2: "체크아웃 모달 열림",
      });
    }
  }, [oProps.isVisible]);

  const navigateExtension = async (type: string) => {
    const oRes = await ApiController.checkout.setConfirmedAtOnLog(
      checkoutFlag.notice_detail_id,
      checkoutFlag.log_id
    );

    if (oRes && oRes.data.ok) {
      if (type === "extension") {
        navigate(`/checkout`, {
          state: {
            storeList: {
              store_id: getRecoil(masterStoreState)!.store_id,
            },
            storeIndex: -1,
          },
        });
      }
    }

    oProps.setVisible(false);
  };

  useEffect(() => {
    const audio = new Audio(getCheckoutAudioSource());
    setCheckoutAudio(audio);

    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, [checkoutNoticeData?.sound_type]);

  useEffect(() => {
    let stopAudioTimer: NodeJS.Timeout | null = null;

    const handleAudioPlay = () => {
      checkoutAudio.currentTime = 0;
      checkoutAudio.play().catch((error) => {
        console.error("error", error);
      });
    };

    if (checkoutAudio) {
      if (oProps.isVisible) {
        handleAudioPlay();
        checkoutAudio.addEventListener("ended", handleAudioPlay);

        stopAudioTimer = setTimeout(() => {
          checkoutAudio.pause();
          checkoutAudio.currentTime = 0;
        }, checkoutNoticeData?.sound_duration_sec! * 1000 || 1000);
      } else {
        checkoutAudio.pause();
        checkoutAudio.currentTime = 0;
      }
    }

    return () => {
      clearTimeout(stopAudioTimer!);
      checkoutAudio.pause();
      checkoutAudio.currentTime = 0;
      checkoutAudio.removeEventListener("ended", handleAudioPlay);
    };
  }, [oProps.isVisible, checkoutAudio]);

  useEffect(() => {
    if (oProps.checkoutNoticeData?.sound_duration_sec <= 180) {
      const timer = setTimeout(() => {
        oProps.setVisible(false);
      }, 5 * 60 * 1000);

      return () => clearTimeout(timer);
    }
  }, [oProps.checkoutNoticeData]);

  return (
    <Modal
      open={oProps.isVisible}
      onClose={onCloseModal}
      center
      classNames={{
        modal: "checkoutModal",
      }}
      showCloseIcon={false}
      animationDuration={150}
      closeOnOverlayClick={false}
    >
      <Style>
        <div className="modal">
          <div className="modal_header">
            <img
              src={`${process.env.PUBLIC_URL}/knockknock/gif/checkout_bell.gif`}
              height={80}
              alt="bell_gif"
            />
            <h2>{langText.checkoutModal_title[lang]}</h2>
            {checkoutNoticeData?.checkout_time !== null && (
              <div className="timeWrap">
                <img
                  className="alarm"
                  src={`${process.env.PUBLIC_URL}/knockknock/svg/ic_alarm.svg`}
                  alt="alarm"
                />
                <h1>{checkoutNoticeData?.checkout_time.slice(0, 5)}</h1>
              </div>
            )}
            {checkoutNoticeData && checkoutNoticeData?.content !== "" && (
              <div className="contentWrap">
                <h3>{getCheckOutContent(checkoutNoticeData, lang)}</h3>
              </div>
            )}
          </div>
          <button
            className="cancelButton"
            onClick={() => navigateExtension("success")}
          >
            {langText.guideModal_button_confirm[lang]}
          </button>
          {checkoutFlag && checkoutFlag.can_purchase_more_time && (
            <button
              className="submitButton"
              onClick={() => navigateExtension("extension")}
            >
              {langText.checkoutModal_button_confirm[lang]}
              <div className="eventBadge">
                <h5>{`🎉 ${langText.checkoutModal_badge_event[lang]}`}</h5>
              </div>
            </button>
          )}
        </div>
        <div className="modalFooter">
          <div className="between">
            <div className="between column">
              <h3>{langText.checkoutModal_footer_one[lang]}</h3>
              <h1>{langText.checkoutModal_footer_two[lang]}</h1>
              <h1 className="large">
                {langText.checkoutModal_footer_three[lang]}
              </h1>
            </div>
            <img
              src={`${process.env.PUBLIC_URL}/knockknock/img/CheckoutCoupon01.png`}
              alt="coupon"
            />
          </div>
        </div>
      </Style>
    </Modal>
  );
};

export default CheckoutModal;

const Style = styled.div`
  @font-face {
    font-family: "Jalnan2TTF";
    src: url("${process.env.PUBLIC_URL}/knockknock/font/Jalnan2TTF.ttf")
      format("truetype");
    font-weight: 400;
  }

  .modal {
    width: 100%;
    height: 100%;
    padding: 4% 8%;
    padding-bottom: 3%;
  }

  .modal_header {
    width: 100%;
    min-height: 200px;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    text-align: center;
    gap: 18px;
    padding-bottom: 20px;

    h2 {
      color: #737373;
      font-weight: 600;
    }
    h3 {
      font-size: 20px;
      line-height: 34px;
      color: #000;
    }

    .timeWrap {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 14px 32px;
      background: #f7f7f7;
      border-radius: 50px;
      margin-bottom: 6px;
      h1 {
        line-height: 32px;
        font-weight: 600;
        color: #40977f;
      }
    }
  }
  .contentWrap {
    width: 100%;
    max-height: 300px;
    overflow-y: scroll;
  }
  .cancelButton {
    margin-top: 6px;
    width: 100%;
    padding: 18px 0;
    background: #ffffff;
    border-radius: 10px;
    color: #979797;
    border: 2px solid #cecece;
    font-size: 24px;
    line-height: 30px;
    font-weight: 600;
  }

  .submitButton {
    position: relative;
    margin-top: 16px;
    width: 100%;
    padding: 18px 0;
    background: #ffffff;
    border-radius: 10px;
    color: #4dd2ae;
    border: 2px solid #4dd2ae;
    font-size: 24px;
    line-height: 30px;
    font-weight: 600;

    .eventBadge {
      position: absolute;
      left: 16px;
      top: -10px;
      display: flex;
      align-items: center;
      padding: 4px 18px;
      background: #fc5f81;
      border-radius: 50px;
      h5 {
        font-size: 14px;
        line-height: 18px;
        font-weight: 600;
        color: #ffffff;
      }
    }
  }

  .modalFooter {
    height: 20vh;
    padding: 8% 8% 10% 12%;
    background: #4dd2ae;
    .between {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .between.column {
      height: 100%;
      flex-direction: column;
      align-items: flex-start;
    }

    h1 {
      font-size: 38px;
      line-height: 45px;
      font-weight: 400;
      color: #fff;
      font-family: "Jalnan2TTF";
      line-height: 65px;
    }
    h1.large {
      font-size: 44px;
      line-height: 52px;
    }
    h3 {
      font-size: 20px;
      color: #fff;
      font-weight: 400;
    }
    img {
      width: 38%;
    }
  }
`;
